/**
 * LogRocket session replay, loaded on demand: the SDK (~160 KB) is a separate chunk that is only fetched once
 * recording actually starts, so it never weighs on first paint. In the native app recording starts when the app
 * is idle a few seconds after launch (not at boot, where its DOM observer would compete with the first render).
 * Calls made before the SDK has loaded are dropped (events, exceptions) or replayed once it has (identity).
 */
import { GUEST_MODE } from '@/guest/guestMode'
import { getAppEnv, isProductionEnv, isStagingEnv } from '@/utils/env'
import { isImpersonatingClient } from '@/utils/impersonationHeaders'
import { isNativePlatform } from '@/utils/platform'
import { companionPolicy } from '@/native/companion'
import { desktopAppVersion, desktopPlatform } from '@/desktop/bridge'

const LOGROCKET_APP_ID = 'mdp7er/lighthouse'

/** Bounces on /?login=true last ~1s; wait longer than that before dwell-init. */
const DWELL_BEFORE_INIT_MS = 5000

const ENGAGE_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const

/**
 * Conservative bot / link-unfurl UA check. Real users who spoof none of these
 * still have to engage or dwell before we start recording.
 */
const AUTOMATED_UA =
    /bot|crawler|spider|crawling|headless|slurp|facebookexternalhit|facebot|pingdom|lighthouse|pagespeed|ahrefs|semrush|whatsapp|telegram|discordbot|slackbot|linkedinbot|twitterbot|applebot|gptbot|chatgpt|claudebot|anthropic|bytespider|skypeuripreview|google-inspectiontool|adsbot|mediapartners|yandex|baiduspider|duckduckbot|ia_archiver|petalbot|dotbot|amazonbot|dataforseo/i

/** Native: start recording this long after launch, once the app is idle. */
const NATIVE_START_DELAY_MS = 4000

type LogRocketSdk = typeof import('logrocket')

let initialized = false
let scheduled = false
let dwellTimer: ReturnType<typeof setTimeout> | undefined
/** The loaded SDK, set once `init` has run. */
let sdk: LogRocketSdk | null = null
let pendingIdentity: [string, Record<string, string | number | boolean>] | null = null

/**
 * LogRocket runs in production and staging (not local/dev/demo/guest tour or staff impersonation), and never in
 * the companion app (decision D-6: no session replay in the store builds).
 */
export function isLogRocketEnabled(): boolean {
    if (GUEST_MODE) return false
    if (!companionPolicy.allowSessionReplay) return false
    if (isImpersonatingClient()) return false
    return isProductionEnv() || isStagingEnv()
}

/** Start recording. Safe to call more than once. */
export function initLogRocket(): void {
    if (initialized || !isLogRocketEnabled() || typeof window === 'undefined') return
    if (isLikelyAutomatedClient()) return

    if (dwellTimer !== undefined) {
        clearTimeout(dwellTimer)
        dwellTimer = undefined
    }

    initialized = true
    void import('logrocket')
        .then(({ default: LogRocket }) => {
            // Brand-mark <img>s must keep HTML width/height (see Logo.vue / index.html).
            // LogRocket replays the raw DOM; a 2160px PNG without those attributes
            // paints at intrinsic size and covers the viewport.
            LogRocket.init(LOGROCKET_APP_ID, {
                release: __LIGHTHOUSE_BUILD__.sha,
                network: { requestSanitizer: withoutSharedContent },
            })
            sdk = LogRocket
            if (pendingIdentity) {
                const [id, traits] = pendingIdentity
                pendingIdentity = null
                LogRocket.identify(id, traits)
            }
        })
        .catch(() => {
            // Blocked (content blocker) or offline: run without replay.
        })
}

/**
 * What the creator shares from their computer through the desktop app (clipboard text in a client-call
 * answer, a screenshot upload) never goes into a recording: those requests are kept without their body.
 */
export function withoutSharedContent<R extends { url: string; body?: string | null }>(request: R): R {
    return /\/sessions\/[^/?#]+\/(?:client-calls\/|attachments(?:[?#]|$))/.test(request.url) ? ({ ...request, body: null } as R) : request
}

/**
 * Do not call LogRocket.init on first paint. Instant anonymous hits to
 * `/?login=true` (crawlers, email/iMessage prefetch, auth-guard redirects
 * from protected routes) were consuming the session quota in ~1s.
 *
 * Recording starts when:
 * - the visitor is identified as signed-in (`identifyLogRocketUser`)
 * - they interact (pointer / key / touch / scroll)
 * - they stay on a non-auth-landing URL for {@link DWELL_BEFORE_INIT_MS}
 *   while the tab is visible (not prerendered / hidden)
 */
export function scheduleLogRocketInit(): void {
    if (scheduled || !isLogRocketEnabled() || typeof window === 'undefined') return
    scheduled = true

    if (isLikelyAutomatedClient()) return

    // Desktop-app users opened the real app: record immediately.
    if (desktopPlatform()) {
        initLogRocket()
        return
    }

    // Capacitor users opened the real app: record, but only once launch has settled.
    if (isNativePlatform()) {
        setTimeout(() => {
            const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback
            if (idle) idle(() => initLogRocket(), { timeout: 3000 })
            else initLogRocket()
        }, NATIVE_START_DELAY_MS)
        return
    }

    const startOnEngage = () => initLogRocket()
    for (const event of ENGAGE_EVENTS) {
        window.addEventListener(event, startOnEngage, { once: true, passive: true })
    }

    if (isWebAuthLandingUrl()) return

    whenDocumentIsActive(() => {
        dwellTimer = setTimeout(() => {
            dwellTimer = undefined
            // Re-check: crawlers often land on /dashboard and get sent here.
            if (isWebAuthLandingUrl()) return
            initLogRocket()
        }, DWELL_BEFORE_INIT_MS)
    })
}

export function captureLogRocketException(
    err: unknown,
    extra?: Record<string, unknown>,
): void {
    if (!sdk || !isLogRocketEnabled()) return
    try {
        sdk.captureException(err as Error, extra ? { extra: extra as Record<string, string | number | boolean> } : undefined)
    } catch {
        // Never block UX if LogRocket fails.
    }
}

/** Custom timeline events in LogRocket session replays. */
export function trackLogRocketEvent(
    name: string,
    properties?: Record<string, string | number | boolean | null | undefined>,
): void {
    if (!sdk || !isLogRocketEnabled()) return
    try {
        const cleaned: Record<string, string | number | boolean> = {}
        if (properties) {
            for (const [key, value] of Object.entries(properties)) {
                if (value !== undefined && value !== null) {
                    cleaned[key] = value
                }
            }
        }
        sdk.track(name, cleaned)
    } catch {
        // Never block UX if LogRocket fails.
    }
}

export function identifyLogRocketUser(
    userId: string,
    traits: Record<string, string | number | boolean | undefined>,
): void {
    if (!isLogRocketEnabled()) return
    // Signed-in users are worth a session (staff impersonation is excluded above). Native already has its
    // recording scheduled for when launch settles; the identity waits for it.
    if (!isNativePlatform()) initLogRocket()
    const cleaned: Record<string, string | number | boolean> = {
        environment: getAppEnv(),
        ...desktopTraits(),
    }
    for (const [key, value] of Object.entries(traits)) {
        if (value !== undefined) {
            cleaned[key] = value
        }
    }
    if (!sdk) {
        pendingIdentity = [userId, cleaned]
        return
    }
    try {
        sdk.identify(userId, cleaned)
    } catch {
        // Never block UX if LogRocket fails.
    }
}

/** Desktop app sessions: `platform` (desktop-mac / -win / -linux) and the app's version. */
function desktopTraits(): Record<string, string> {
    const os = desktopPlatform()
    if (!os) return {}
    return {
        platform: `desktop-${os}`,
        app_version: desktopAppVersion() ?? 'unknown',
    }
}

function isLikelyAutomatedClient(): boolean {
    if (typeof navigator === 'undefined') return true
    if (navigator.webdriver) return true
    const ua = navigator.userAgent
    if (!ua) return true
    return AUTOMATED_UA.test(ua)
}

/** `/login` redirects to `/?login=true`; `/launch` is a landing alias. */
function isWebAuthLandingUrl(): boolean {
    if (typeof window === 'undefined') return false
    const path = window.location.pathname.replace(/\/+$/, '') || '/'
    if (path === '/login' || path === '/native-login') return true
    if (path !== '/' && path !== '/launch') return false
    const query = new URLSearchParams(window.location.search)
    return query.get('login') === 'true' || query.get('signup') === 'true'
}

function whenDocumentIsActive(callback: () => void): void {
    const isActive = () => {
        const prerendering =
            'prerendering' in document &&
            Boolean((document as Document & { prerendering?: boolean }).prerendering)
        return document.visibilityState === 'visible' && !prerendering
    }

    if (isActive()) {
        callback()
        return
    }

    const onChange = () => {
        if (!isActive()) return
        document.removeEventListener('visibilitychange', onChange)
        document.removeEventListener('prerenderingchange', onChange)
        callback()
    }
    document.addEventListener('visibilitychange', onChange)
    document.addEventListener('prerenderingchange', onChange)
}
